import React, { useEffect, useState } from "react";
import './OpalCoverageLayout.css';
import CoverageSidebar from "./CoverageSidebar/CoverageSidebar";
import CoverageView from "./CoverageView/CoverageView";
import { Button } from "react-bootstrap";
import TauriImage from "../TauriImage/TauriImage";
import { useParams } from "react-router-dom";
import { getRecordById, updateRecord } from "../../localDB";
import { activityClassification, sessionType, statusMapping } from "../../visualizations/visualizations";
import { useBaseProvider } from "../../BaseManager/BaseManager";
import { HttpTransportType, HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import appURL from '../../appURL.json';
import EventView from "./CoverageView/EventView/EventView";

const getTodayWithTimezoneOffset = () => {
    const now = new Date();
    now.setHours(0, 0, 0, 0); // Ensuring time does not affect date selection
    return now;
};

const OpalCoverageLayout = () => {

    const { id } = useParams();

    const [item, setItem] = useState({});
    const { isSidebarCollapsed, setIsSidebarCollapsed, signalRConnection } = useBaseProvider();
    const [selectedDate, setSelectedDate] = useState(getTodayWithTimezoneOffset());
    const [sidebarLoading, setSidebarLoading] = useState(false);
    const [startTime, setStartTime] = useState(null);
    const [elapsedText, setElapsedText] = useState('');
    const [isMD, setIsMD] = useState(window.innerWidth <= 768);

    useEffect(() => {
        if (!signalRConnection) return;

        const getSessionStatus = (session) => {
            if (session.Type === sessionType.Recovery) {
                return statusMapping.recovered;
            } else if (session.Type === sessionType.Advanced) {
                return statusMapping.advanced;
            } else {
                return statusMapping.success;
            }
        };

        const handleSessionStartSignalR = async (s) => {
            if (!s) return;

            const sessionID = Number(s.sessionID);
            const session = await getRecordById('session', sessionID);
            if (!session) return;

            const ActualStart = s.timestamp
                ? new Date(s.timestamp).toISOString()
                : new Date().toISOString();

            const Type = sessionType[s?.type] ?? sessionType.Regular;
            const Status = statusMapping.inprogress;

            session.ActualStart = ActualStart;
            session.Type = Type;
            session.Status = Status;

            await updateRecord('session', session);

            if (sessionID === Number(id)) {
                setItem((prev) => ({
                    ...prev,
                    ActualStart,
                    Type,
                    Status
                }));
            }
        };

        const handleSessionEndSignalR = async (s) => {
            if (!s) return;

            const sessionID = Number(s.sessionID);
            const session = await getRecordById('session', sessionID);
            if (!session) return;

            const ActualEnd = s.timestamp
                ? new Date(s.timestamp).toISOString()
                : new Date().toISOString();

            session.ActualEnd = ActualEnd;

            const start = new Date(session.ActualStart);
            const end = new Date(s.timestamp || new Date());

            const durationMs = end - start;
            session.Duration = +(durationMs / 1000).toFixed(4);

            session.Status = getSessionStatus(session);

            await updateRecord('session', session);

            if (sessionID === Number(id)) {
                setItem((prev) => ({
                    ...prev,
                    ActualEnd,
                    Duration: session.Duration,
                    Status: session.Status,
                }));
            }
        };

        const handleSessionMarkAsJointCallSignalR = async (sessionIds) => {
            if (!Array.isArray(sessionIds) || sessionIds.length === 0) return;

            for (const sID of sessionIds) {
                const sessionID = Number(sID);
                const session = await getRecordById("session", sessionID);
                if (!session) continue;

                // Mark as joint
                session.Joint = true;

                // Save the updated record
                await updateRecord("session", session);

                if (sessionID === Number(id)) {
                    setItem(prev => ({
                        ...prev,
                        Joint: true
                    }));
                }
            }
        };


        signalRConnection.on("SessionStarted", handleSessionStartSignalR);
        signalRConnection.on("SessionEnded", handleSessionEndSignalR);
        signalRConnection.on("MarkAsJointCall", handleSessionMarkAsJointCallSignalR);

        return () => {
            signalRConnection.off("SessionStarted", handleSessionStartSignalR);
            signalRConnection.off("SessionEnded", handleSessionEndSignalR);
            signalRConnection.off("MarkAsJointCall", handleSessionMarkAsJointCallSignalR);
        };
    }, [signalRConnection, id]);

    useEffect(() => {
        if (!startTime) {
            setElapsedText('');
            return;
        }

        const interval = setInterval(() => {
            setElapsedText(getElapsedTime(startTime));
        }, 60000);

        // Set initial value
        setElapsedText(getElapsedTime(startTime));

        return () => clearInterval(interval);
    }, [startTime]);

    const getStartTime = async () => {
        const session = await getRecordById("session", Number(id));
        if (session && session.Status === statusMapping.inprogress) {
            setStartTime(session?.ActualStart);
        } else {
            setElapsedText('');
            setStartTime(null);
        }
    }

    useEffect(() => {
        const handleResize = () => {
            setIsMD(window.innerWidth <= 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);


    useEffect(() => {
        const handleResize = () => {
            setIsMD(window.innerWidth <= 768);
        };

        window.addEventListener("resize", handleResize);
        handleResize(); // initialize state on mount
    }, []);


    useEffect(() => {
        if (!id) return;
        getStartTime();
    }, [id]);

    useEffect(() => {
        if (isSidebarCollapsed) {
            getStartTime();
        }
    }, [isSidebarCollapsed])

    const onSelectItem = (c) => {
        setItem(c);

        const sidebarMenu = document.getElementById('sidebarMenu');
        if (sidebarMenu && sidebarMenu.classList.contains('show')) {
            sidebarMenu.classList.remove('show');
        }
    }

    function getElapsedTime(startISO) {
        const start = new Date(startISO);
        const now = new Date();
        const diffMs = now - start;
        const diffMins = Math.floor(diffMs / 60000);

        if (diffMins < 1) return "just now";
        if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
        const diffHrs = Math.floor(diffMins / 60);
        if (diffHrs < 24) return `${diffHrs} hour${diffHrs > 1 ? 's' : ''} ago`;
        const diffDays = Math.floor(diffHrs / 24);
        return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    }


    return (
        <div className="opal-coverage-layout-container d-flex h-100 row">
            <nav
                id="sidebarMenu"
                className={`${isSidebarCollapsed && !isMD ? 'collapsed-sidebar' : 'col-md-3 col-lg-3 col-xl-3 col-xxl-2 d-md-block'} p-0 sidebar-layout collapse`}
            >
                <div className="position-sticky w-100 h-100">
                    <CoverageSidebar
                        onSelectItem={onSelectItem}
                        isSidebarCollapsed={isSidebarCollapsed}
                        setIsSidebarCollapsed={setIsSidebarCollapsed}
                        selectedItem={id}
                        selectedDate={selectedDate}
                        setSelectedDate={setSelectedDate}
                        loading={sidebarLoading}
                        setLoading={setSidebarLoading}
                        isMD={isMD}
                    />

                    {isSidebarCollapsed && (
                        <div className="sidebar-minimize h-100" onClick={() => setIsSidebarCollapsed(false)}>
                            <Button
                                className="btn w-100 d-none d-md-block"
                                onClick={() => setIsSidebarCollapsed(true)}
                            >
                                <TauriImage
                                    localImage="/Images/solar--double-alt-arrow-right-outline.svg"
                                    alt="icon"
                                    color="#1D5D9B"
                                />
                            </Button>

                            <div className="rotate-label">
                                {startTime ? (
                                    <>
                                        <div className="session-status">On Going Session</div>
                                        <div className="session-time">
                                            <span className="rotate-bold">Started : </span>
                                            <span>{elapsedText || '—'}</span>
                                        </div>
                                    </>
                                ) : (
                                    <div className="session-status"></div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </nav>
            <main className="flex-grow-1 col-md-9 col-lg-9 p-0 m-0">
                <div className='host-container' tag="main">
                    <div className="flex-grow-1">
                        {id && (
                            item?.Activity?.Classification === activityClassification.Event ? (
                                <EventView
                                    id={id}
                                    setStartTime={setStartTime}
                                    selectedSession={item}
                                    sidebarLoading={sidebarLoading}
                                />
                            ) : (
                                <CoverageView
                                    id={id}
                                    setStartTime={setStartTime}
                                    selectedSession={item}
                                    sidebarLoading={sidebarLoading}
                                />
                            )
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
};

export default OpalCoverageLayout;
